import React from "react";
import { Row, Col } from "react-bootstrap";
import { sparketGold, sparketLightGray, WITHDRAWAL_FEE_MODE, FeeMode } from "utils/constants.ts";
import { WithdrawalPreviewData } from "interfaces/withdrawal-preview.ts";
import { PulseLoader } from "react-spinners";

interface WithdrawalSummaryBlockProps {
  amount: number | undefined;
  feeMode: FeeMode;
  onSettingsClick?: () => void;
  onWithdraw: () => void;
  disabled: boolean;
  processing: boolean;
  summaryDisabled?: boolean;
  previewData?: WithdrawalPreviewData | null;
  previewLoading?: boolean;
  showFeeOptions?: boolean;
}

const WithdrawalSummaryBlock: React.FC<WithdrawalSummaryBlockProps> = ({
  amount,
  feeMode,
  onSettingsClick,
  onWithdraw,
  disabled,
  processing,
  summaryDisabled = false,
  previewData,
  previewLoading = false,
  showFeeOptions = true,
}) => {
  const withdrawAmount = amount || 0;
  
  // Use preview data if available, otherwise calculate locally
  const feeAmount = previewData?.fee_amount ?? (withdrawAmount * 0.03);
  const amountDeductedFromBalance = previewData?.amount_deducted_from_balance ?? 
    (feeMode === "add" ? withdrawAmount + feeAmount : withdrawAmount);
  const amountSentToUser = previewData?.amount_sent_to_user ?? 
    (feeMode === "deduct" ? withdrawAmount - feeAmount : withdrawAmount);

  // Helper function to format amounts without unnecessary decimals
  const formatAmount = (value: number) => {
    return value % 1 === 0 ? value.toString() : value.toFixed(2);
  };

  return (
    <div
      style={{
        backgroundColor: summaryDisabled ? "rgba(0, 0, 0, 0.15)" : "rgba(0, 0, 0, 0.3)",
        borderRadius: "8px",
        padding: "16px",
        marginTop: "20px",
        opacity: summaryDisabled ? 0.6 : 1,
      }}
    >
      {/* Minimalistic Fee Information */}
      <div style={{ marginBottom: "16px" }}>
        {/* Withdrawal Fees Row */}
        <Row style={{ marginBottom: "8px" }}>
          <Col xs={7}>
            <span style={{ color: summaryDisabled ? "#555" : sparketLightGray, fontSize: "0.85em", whiteSpace: "nowrap" }}>
              {feeMode === WITHDRAWAL_FEE_MODE.ADD ? "Added" : "Deducted"} fees (3%)
              {showFeeOptions && (
                <>
                  {" "}•{" "}
                  <button
                    onClick={summaryDisabled ? undefined : onSettingsClick}
                    disabled={summaryDisabled}
                    style={{
                      backgroundColor: "transparent",
                      color: summaryDisabled ? "#444" : sparketGold,
                      border: "none",
                      padding: "0",
                      cursor: summaryDisabled ? "default" : "pointer",
                      textDecoration: summaryDisabled ? "none" : "underline",
                      fontWeight: "600",
                      fontSize: "1em",
                    }}
                  >
                    Fee options
                  </button>
                </>
              )}
            </span>
          </Col>
          <Col xs={5} style={{ textAlign: "right" }}>
            <span style={{ color: summaryDisabled ? "#555" : "white", fontSize: "0.85em" }}>
              {summaryDisabled ? "—" : previewLoading ? (
                <PulseLoader size={4} color={sparketLightGray} speedMultiplier={0.8} />
              ) : (withdrawAmount > 0 ? `${formatAmount(feeAmount)} USD` : "—")}
            </span>
          </Col>
        </Row>

        {/* Amount Received Row with Settings */}
        <Row style={{ marginBottom: feeMode === "add" ? "8px" : "0" }}>
          <Col xs={7}>
            <span style={{ color: summaryDisabled ? "#555" : "white", fontSize: "0.9em", fontWeight: "600" }}>
              Amount Received
            </span>
          </Col>
          <Col xs={5} style={{ textAlign: "right" }}>
            <span style={{ color: summaryDisabled ? "#555" : sparketGold, fontSize: "0.95em", fontWeight: "600" }}>
              {summaryDisabled ? "—" : previewLoading ? (
                <PulseLoader size={5} color={sparketGold} speedMultiplier={0.8} />
              ) : (withdrawAmount > 0 ? `${formatAmount(amountSentToUser)} USD` : "—")}
            </span>
          </Col>
        </Row>

        {/* Total Charged Row (only when adding fee) */}
        {feeMode === WITHDRAWAL_FEE_MODE.ADD && (
          <Row>
            <Col xs={7}>
              <span style={{ color: summaryDisabled ? "#555" : "#ff6b6b", fontSize: "0.8em" }}>
                Total deducted from your balance
              </span>
            </Col>
            <Col xs={5} style={{ textAlign: "right" }}>
              <span style={{ color: summaryDisabled ? "#555" : "#ff6b6b", fontSize: "0.85em" }}>
                {summaryDisabled ? "—" : previewLoading ? (
                  <PulseLoader size={4} color="#ff6b6b" speedMultiplier={0.8} />
                ) : (withdrawAmount > 0 ? `${formatAmount(amountDeductedFromBalance)} USD` : "—")}
              </span>
            </Col>
          </Row>
        )}
      </div>

      {/* Withdraw Button */}
      <button
        onClick={onWithdraw}
        disabled={disabled || processing}
        style={{
          width: "100%",
          padding: "12px",
          backgroundColor: disabled || processing ? "#444" : sparketGold,
          color: disabled || processing ? "#888" : "black",
          border: "none",
          borderRadius: "6px",
          fontSize: "15px",
          fontWeight: "600",
          cursor: disabled || processing ? "not-allowed" : "pointer",
          transition: "all 0.15s ease",
          marginTop: "16px",
        }}
      >
        {processing ? "Processing..." : "Withdraw"}
      </button>
    </div>
  );
};

export default WithdrawalSummaryBlock;